import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Form } from "react-bootstrap";
import { toast } from "react-toastify";

import {
  ACCEPTED_GALLERY_MIME_TYPES,
  MAX_GALLERY_UPLOAD_BYTES,
  completeMatchUpload,
  configureMatchGallery,
  getMatchPhotosAdmin,
  initiateMatchUploads,
  listMatchFeatureToggles,
  setMatchFeatureToggle,
  setMatchPreviews,
  type MatchGalleryAdminPage,
} from "../../../api/tournament/galleryService";
import { useUploadQueue, type CompleteResult } from "./useUploadQueue";
import { useProcessingPoller } from "./useProcessingPoller";

import "./Gallery.css";

const MATCH_GALLERY_FEATURE = "MATCH_GALLERY";
const MAX_FILES_PER_BATCH = 50;

const EMPTY_GALLERY = (matchId: string): MatchGalleryAdminPage => ({
  matchId,
  resourceKey: `match_photos:${matchId}`,
  accessType: "paid",
  allowDownload: false,
  freePreviewPhotoIds: [],
  freePreviewCount: 0,
  items: [],
  pagination: { page: 1, limit: 30, total: 0, totalPages: 1 },
});

interface PendingFile {
  file: File;
  isFreePreview: boolean;
}

interface MatchGallerySectionProps {
  matchId: string;
  sport: string;
}

const MatchGallerySection: React.FC<MatchGallerySectionProps> = ({ matchId, sport }) => {
  const [gallery, setGallery] = useState<MatchGalleryAdminPage>(EMPTY_GALLERY(matchId));
  const [loading, setLoading] = useState(true);

  const [accessType, setAccessType] = useState<"public" | "paid">("paid");
  const [priceInr, setPriceInr] = useState("");
  const [savingAccess, setSavingAccess] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);

  const [allowDownload, setAllowDownload] = useState(false);
  const [savingDownload, setSavingDownload] = useState(false);

  const [galleryEnabled, setGalleryEnabled] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [selectedPreviewIds, setSelectedPreviewIds] = useState<string[]>([]);
  const [savingPreviews, setSavingPreviews] = useState(false);

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMatchPhotosAdmin(matchId, 1, 100);
      setGallery(res);
      setAccessType(res.accessType);
      setAllowDownload(res.allowDownload);
      setPriceInr(res.pricing?.IN?.amount?.toString() ?? "");
      setSelectedPreviewIds(res.freePreviewPhotoIds ?? []);
      return res;
    } catch {
      // No gallery rows exist yet for this match — treat as a fresh, empty
      // gallery rather than a hard error so uploading is still available.
      const empty = EMPTY_GALLERY(matchId);
      setGallery(empty);
      return empty;
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  const fetchToggle = useCallback(async () => {
    try {
      const toggles = await listMatchFeatureToggles(sport, matchId);
      const toggle = toggles.find((t) => t.name === MATCH_GALLERY_FEATURE);
      setGalleryEnabled(!!toggle?.enabled);
    } catch {
      // Feature-toggle API may not be available for every sport/match yet —
      // leave the switch off rather than blocking the rest of the section.
      setGalleryEnabled(false);
    }
  }, [sport, matchId]);

  useEffect(() => {
    fetchGallery();
    fetchToggle();
  }, [fetchGallery, fetchToggle]);

  const readyItems = useMemo(
    () => gallery.items.filter((p) => !p.status || p.status === "ready"),
    [gallery.items],
  );
  const hasUploadedAny = gallery.items.length > 0;
  const hasReadyPhoto = readyItems.length > 0;

  const handleCompleteUpload = useCallback(
    async (photoId: string): Promise<CompleteResult> => {
      const result = await completeMatchUpload(matchId, photoId);
      return { status: result.status };
    },
    [matchId],
  );

  const {
    states: uploadStates,
    run: runUploadQueue,
    reset: resetUploadQueue,
    setItemStatus,
  } = useUploadQueue(handleCompleteUpload);
  const { start: startPolling } = useProcessingPoller();

  const saveAccessSettings = async () => {
    setSavingAccess(true);
    setAccessError(null);
    try {
      if (accessType === "paid") {
        const amount = Number(priceInr);
        if (!amount || amount <= 0) {
          throw new Error("Enter a valid INR price for a paid match gallery.");
        }
        await configureMatchGallery(matchId, {
          accessType: "paid",
          pricing: { IN: { amount, currency: "INR" } },
        });
      } else {
        await configureMatchGallery(matchId, { accessType: "public" });
      }
      toast.success(`Match gallery set to ${accessType === "public" ? "Public" : "Paid"}.`);
      await fetchGallery();
    } catch (err) {
      setAccessError(err instanceof Error ? err.message : "Failed to save the gallery access type.");
    } finally {
      setSavingAccess(false);
    }
  };

  const saveAllowDownload = async (next: boolean) => {
    setSavingDownload(true);
    try {
      await configureMatchGallery(matchId, { allowDownload: next });
      setAllowDownload(next);
      toast.success(next ? "Downloads allowed." : "Downloads hidden.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update the download setting.");
    } finally {
      setSavingDownload(false);
    }
  };

  const toggleGalleryTab = async (enabled: boolean) => {
    setSavingToggle(true);
    try {
      await setMatchFeatureToggle(sport, matchId, MATCH_GALLERY_FEATURE, enabled);
      setGalleryEnabled(enabled);
      toast.success(enabled ? "Gallery tab enabled." : "Gallery tab hidden.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update the Gallery tab.");
    } finally {
      setSavingToggle(false);
    }
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const valid: PendingFile[] = [];
    const rejected: string[] = [];
    files.forEach((file) => {
      if (!ACCEPTED_GALLERY_MIME_TYPES.includes(file.type)) {
        rejected.push(`${file.name} (unsupported type)`);
        return;
      }
      if (file.size > MAX_GALLERY_UPLOAD_BYTES) {
        rejected.push(`${file.name} (larger than 20 MB)`);
        return;
      }
      valid.push({ file, isFreePreview: false });
    });
    setPendingFiles((prev) => {
      const merged = [...prev, ...valid];
      if (merged.length > MAX_FILES_PER_BATCH) {
        rejected.push(`Only the first ${MAX_FILES_PER_BATCH} files are kept per batch.`);
        return merged.slice(0, MAX_FILES_PER_BATCH);
      }
      return merged;
    });
    setUploadError(rejected.length ? `Skipped: ${rejected.join(", ")}` : null);
    event.target.value = "";
  };

  const togglePendingFreePreview = (index: number) => {
    setPendingFiles((prev) =>
      prev.map((item, i) => (i === index ? { ...item, isFreePreview: !item.isFreePreview } : item)),
    );
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const startUpload = async () => {
    if (pendingFiles.length === 0) return;
    setUploading(true);
    setUploadError(null);
    resetUploadQueue();
    try {
      const initiated = await initiateMatchUploads(
        matchId,
        sport,
        pendingFiles.map(({ file, isFreePreview }) => ({
          fileName: file.name,
          contentType: file.type,
          isFreePreview,
        })),
      );
      const items = initiated.items.map((item, index) => ({
        id: item.photoId,
        file: pendingFiles[index].file,
        isFreePreview: item.isFreePreview,
        uploadUrl: item.uploadUrl,
        requiredHeaders: item.requiredHeaders,
      }));
      await runUploadQueue(items);
      setPendingFiles([]);

      // `complete` only queues background processing (202) — refresh the
      // admin listing and poll every still-processing photo until it
      // settles to ready/failed (guide §20).
      const freshGallery = await fetchGallery();
      const uploadedIds = new Set(items.map((i) => i.id));
      const pendingIds = freshGallery.items
        .filter(
          (item) =>
            uploadedIds.has(item.id) &&
            item.status &&
            item.status !== "ready" &&
            item.status !== "failed",
        )
        .map((item) => item.id);

      if (pendingIds.length > 0) {
        startPolling(
          pendingIds,
          async () => {
            const res = await getMatchPhotosAdmin(matchId, 1, 100);
            setGallery(res);
            return res.items.map((item) => ({
              id: item.id,
              status: item.status ?? "ready",
              processingError: item.processingError,
            }));
          },
          (id, status, processingError) => {
            setItemStatus(id, status === "ready" ? "ready" : "failed", processingError);
          },
        );
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Failed to start the upload.");
    } finally {
      setUploading(false);
    }
  };

  const togglePreviewSelection = (photoId: string, status?: string) => {
    // Only a fully processed photo can be selected as a free preview.
    if (status && status !== "ready") return;
    setSelectedPreviewIds((prev) =>
      prev.includes(photoId) ? prev.filter((id) => id !== photoId) : [...prev, photoId],
    );
  };

  const savePreviews = async () => {
    setSavingPreviews(true);
    try {
      await setMatchPreviews(matchId, selectedPreviewIds);
      toast.success("Free photos updated.");
      await fetchGallery();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update free photos.");
    } finally {
      setSavingPreviews(false);
    }
  };

  return (
    <Card className="mb-4" style={{ border: "1px solid #e0e0e0", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
      <Card.Header
        className="d-flex justify-content-between align-items-center"
        style={{ backgroundColor: "#f8f9fc", borderBottom: "1px solid #e0e0e0" }}
      >
        <h5 className="mb-0"><i className="ri-image-2-line me-2" />Match Gallery</h5>
        <Form.Check
          type="switch"
          id="match-gallery-toggle"
          label="Show Gallery tab"
          checked={galleryEnabled}
          disabled={savingToggle || !hasReadyPhoto}
          onChange={(e) => toggleGalleryTab(e.target.checked)}
        />
      </Card.Header>
      <Card.Body>
        {!hasReadyPhoto && (
          <Alert variant="info" className="mb-3">
            Upload and process at least one photo before the Gallery tab can be shown to viewers.
          </Alert>
        )}

        {accessError && <Alert variant="danger">{accessError}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Access</Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              id="match-access-paid"
              label="Paid (Match Photos Pass required)"
              checked={accessType === "paid"}
              disabled={savingAccess || !hasUploadedAny}
              onChange={() => setAccessType("paid")}
            />
            <Form.Check
              inline
              type="radio"
              id="match-access-public"
              label="Public (free for everyone)"
              checked={accessType === "public"}
              disabled={savingAccess || !hasUploadedAny}
              onChange={() => setAccessType("public")}
            />
          </div>
          {!hasUploadedAny && (
            <Form.Text className="text-muted">
              Upload at least one photo first — access can then be configured.
            </Form.Text>
          )}

          {accessType === "paid" && hasUploadedAny && (
            <Form.Group className="mt-2" style={{ maxWidth: 220 }}>
              <Form.Label className="small text-muted mb-1">Price (INR)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={priceInr}
                onChange={(e) => setPriceInr(e.target.value)}
                placeholder="39"
                disabled={savingAccess}
              />
            </Form.Group>
          )}

          {hasUploadedAny && (
            <Button className="mt-3" size="sm" onClick={saveAccessSettings} disabled={savingAccess}>
              {savingAccess ? "Saving…" : "Save Access Settings"}
            </Button>
          )}
        </Form.Group>

        <Form.Check
          type="switch"
          id="match-allow-download"
          className="mb-4"
          label="Allow download"
          checked={allowDownload}
          disabled={savingDownload || !hasUploadedAny}
          onChange={(e) => saveAllowDownload(e.target.checked)}
        />

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Upload Photos</Form.Label>
          {uploadError && <Alert variant="warning">{uploadError}</Alert>}
          <Form.Control
            type="file"
            accept={ACCEPTED_GALLERY_MIME_TYPES.join(",")}
            multiple
            onChange={handleFilesSelected}
            disabled={uploading}
          />
          <Form.Text className="text-muted">
            JPEG, PNG, WebP or AVIF — up to {MAX_FILES_PER_BATCH} files per batch, 20 MB each.
          </Form.Text>
        </Form.Group>

        {pendingFiles.map((item, index) => (
          <div key={`${item.file.name}-${index}`} className="gallery-upload-row">
            <span className="flex-grow-1">{item.file.name}</span>
            <Form.Check
              type="checkbox"
              label="Free"
              checked={item.isFreePreview}
              onChange={() => togglePendingFreePreview(index)}
              disabled={uploading}
            />
            <Button size="sm" variant="outline-danger" onClick={() => removePendingFile(index)} disabled={uploading}>
              Remove
            </Button>
          </div>
        ))}

        {pendingFiles.length > 0 && (
          <Button className="mt-2 mb-3" onClick={startUpload} disabled={uploading}>
            {uploading ? "Uploading…" : `Upload ${pendingFiles.length} photo(s)`}
          </Button>
        )}

        {uploadStates.length > 0 && (
          <div className="mb-3">
            {uploadStates.map((state) => (
              <div key={state.id} className="gallery-upload-row" title={state.error}>
                <span className="flex-grow-1">{state.fileName}</span>
                {state.isFreePreview && <Badge bg="info">Free</Badge>}
                <Badge bg={state.status === "ready" ? "success" : state.status === "failed" ? "danger" : "secondary"}>
                  {state.status}
                </Badge>
              </div>
            ))}
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-2">
          <Form.Label className="fw-semibold mb-0">
            Photos ({loading ? "…" : readyItems.length}/{gallery.items.length})
          </Form.Label>
          {accessType === "paid" && hasReadyPhoto && (
            <Button size="sm" onClick={savePreviews} disabled={savingPreviews}>
              {savingPreviews ? "Saving…" : `Save Free Photos (${selectedPreviewIds.length})`}
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-muted text-center py-4">Loading…</div>
        ) : gallery.items.length === 0 ? (
          <div className="text-muted text-center py-4">No photos uploaded yet.</div>
        ) : (
          <div className="gallery-media-grid">
            {gallery.items.map((photo) => {
              const isSelected = selectedPreviewIds.includes(photo.id);
              const isReady = !photo.status || photo.status === "ready";
              const isFailed = photo.status === "failed";
              return (
                <div
                  key={photo.id}
                  className="gallery-media-tile"
                  onClick={() => accessType === "paid" && togglePreviewSelection(photo.id, photo.status)}
                  role={accessType === "paid" && isReady ? "button" : undefined}
                  title={isFailed ? photo.processingError : undefined}
                  style={{ backgroundColor: photo.placeholder?.dominantColor || "#eef1f5" }}
                >
                  {photo.contentUrl && (
                    // eslint-disable-next-line jsx-a11y/alt-text
                    <img src={photo.contentUrl} alt="Match" />
                  )}
                  {photo.isFree && isReady && <span className="gallery-media-tile__badge">Free</span>}
                  {!isReady && !isFailed && (
                    <div className="gallery-media-tile__status">
                      {photo.status === "queued" ? "Queued" : "Processing…"}
                    </div>
                  )}
                  {isFailed && (
                    <div
                      className="gallery-media-tile__status"
                      style={{ background: "rgba(176,0,32,0.7)" }}
                    >
                      Failed
                    </div>
                  )}
                  {accessType === "paid" && isReady && (
                    <Form.Check
                      className="gallery-media-tile__check"
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => togglePreviewSelection(photo.id, photo.status)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default MatchGallerySection;
